<template>
    <div class="relative flex w-full flex-col">
        <div
            v-if="isDefaultMessage"
            class="text-md"
        >
            Are you sure you want to delete this {{ itemType }}?
        </div>
        <div
            v-else
            class="text-md"
        >
            {{ customMessage }}
        </div>
        <div class="container mt-3 divide-y divide-gray-200 rounded-muval-2 bg-muval-gray-5 dark:bg-gray-900">
            <div class="grid grid-cols-5 gap-y-2 p-4 text-sm">
                <span class="font-medium">{{ itemType }}</span>
                <span class="col-span-4">{{ data.id }}</span>
                <template
                    v-for="item in deleteItemSchema"
                    :key="item"
                >
                    <span class="font-medium">{{ item.name }}</span>
                    <!-- Thankyou Stack Overflow: https://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-and-arrays-by-string-path -->
                    <span class="col-span-4">
                        {{ `${item.value}`.split('.').reduce((p, c) => (p && p[c]) || null, data) }}
                    </span>
                </template>
                <template v-if="data.created_at">
                    <span class="font-medium">Created</span>
                    <time class="col-span-4">
                        {{ $dayjs(data.created_at).format('dddd DD MMM YYYY') }} at
                        {{ $dayjs(data.created_at).format('h:mm A') }}
                    </time>
                </template>
            </div>
        </div>
        <div class="form-save mt-4">
            <MuvalButton
                quaternary
                @click="closeModal"
            >
                No, keep this item
            </MuvalButton>
            <div>
                <MuvalButton
                    :loading="loading"
                    @click="remove"
                >
                    {{ customConfirmButtonText ? customConfirmButtonText : 'Yes, delete this ' + itemType }}
                </MuvalButton>
            </div>
        </div>
    </div>
</template>

<script setup>
import useEmitter from '@plugins/useEmitter';
import MuvalButton from '@buttons/MuvalButton.vue';
import { toast } from 'vue-sonner';

const props = defineProps({
    itemIndex: {
        type: Number,
        required: true,
    },
    itemType: {
        type: [String, null],
        required: false,
        default: null,
    },
    data: {
        type: [Object, null],
        required: false,
        default: null,
    },
    url: {
        type: [String, null],
        required: true,
        default: null,
    },
    deleteItemSchema: {
        type: [Array, null],
        required: false,
        default: null,
    },
    emitKey: {
        type: [String, null],
        required: false,
        default: null,
    },
    scope: {
        type: String,
        required: false,
        default: 'tenant',
    },
    isDefaultMessage: {
        type: Boolean,
        required: false,
        default: true,
    },
    customMessage: {
        type: String,
        required: false,
        default: null,
    },
    customConfirmButtonText: {
        type: String,
        required: false,
        default: null,
    },
});

const axios = computed(() => {
    if (props.scope === 'tenant') {
        return window.tenantAxios;
    } else {
        return window.centralAxios;
    }
});

const emit = defineEmits(['close:modal', 'remove:item']);
const loading = ref(false);
const emitter = useEmitter();

const lastDeleted = ref({
    index: null,
    id: null,
});

async function remove() {
    try {
        loading.value = true;

        const response = await axios.value.delete(`${props.url}/${props.data.id}`);

        loading.value = false;

        if (response) {
            lastDeleted.value.index = props.itemIndex;
            lastDeleted.value.id = props.data.id;

            console.log(lastDeleted.value);

            toast('Item has been deleted.', {
                action: {
                    label: 'Undo',
                    onClick: async () => {
                        const response = await axios.value.put(`${props.url}/restore/${lastDeleted.value.id}`);
                        if (response) {
                            emitter.emit(props.emitKey ? `restore-item-${props.emitKey}` : 'restore-item', {
                                index: lastDeleted.value.index,
                                object: response.data.restored,
                            });
                            toast(
                                `${props.itemType.toUpperCase().substring(0, 1) + props.itemType.toLowerCase().substring(1)} restored.`,
                            );
                        }
                    },
                },
            });

            if (props.emitKey) {
                emitter.emit(`remove:item-${props.emitKey}`, props.data.id);
            } else {
                emit('remove:item', props.data.id);
            }
        }

        closeModal();
    } catch (error) {
        loading.value = false;
        console.log('DeleteModal@error', error);

        // Display the error message in the toast
        let errorMessage = null;
        if (error?.message) {
            errorMessage = error?.message;
        } else if (error?.data?.error) {
            errorMessage = error?.data?.error;
        } else {
            errorMessage = 'Failed to delete item. Please try again.';
        }

        toast.error(errorMessage);
    }
}

function closeModal() {
    emit('close:modal');
}
</script>
